import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import PermissionGuard from '../../components/PermissionGuard';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { purchaseOrderService, PurchaseOrderDTO, PurchaseOrderStatus, ApprovalStatus } from '../../services/purchaseOrderService';
import './PurchaseOrderPage.css';
import { CirclePlus, Eye, RefreshCw, SendHorizontal, FileText, PenLine, Trash2, Clock, Check, Banknote } from 'lucide-react';

const STATUS_BADGE: Record<PurchaseOrderStatus, string> = {
  Draft:             'ims-badge ims-badge-draft',
  Submitted:         'ims-badge ims-badge-submitted',
  Confirmed:         'ims-badge ims-badge-confirmed',
  PartiallyReceived: 'ims-badge ims-badge-partially-received',
  Received:          'ims-badge ims-badge-received',
  Closed:            'ims-badge ims-badge-closed',
};

const STATUS_I18N: Record<PurchaseOrderStatus, string> = {
  Draft:             'purchaseOrder.statusDraft',
  Submitted:         'purchaseOrder.statusSubmitted',
  Confirmed:         'purchaseOrder.statusConfirmed',
  PartiallyReceived: 'purchaseOrder.statusPartiallyReceived',
  Received:          'purchaseOrder.statusReceived',
  Closed:            'purchaseOrder.statusClosed',
};

const APPROVAL_BADGE: Record<ApprovalStatus, string> = {
  Draft:          'ims-badge ims-badge-draft',
  PendingApproval:'ims-badge ims-badge-pending',
  Approved:       'ims-badge ims-badge-approved',
  Rejected:       'ims-badge ims-badge-rejected',
};

const PurchaseOrderListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [orders, setOrders] = useState<PurchaseOrderDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submittingId, setSubmittingId] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'draft' | 'pending' | 'approved'>('all');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as any)?.message || null
  );

  useEffect(() => {
    loadOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await purchaseOrderService.getPurchaseOrders({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setOrders(data);
      }
      setLoading(false);
    } catch {
      setError(t('purchaseOrder.failedToLoad'));
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await purchaseOrderService.getPurchaseOrders({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) {
        setOrders(data);
      }
    } catch {
      setError(t('purchaseOrder.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubmitForApproval = async (item: PurchaseOrderDTO) => {
    setSubmittingId(item.id);
    setError(null);
    try {
      await purchaseOrderService.submitPurchaseOrder(item.id);
      setSuccessMessage(t('purchaseOrder.submittedSuccess'));
      // Refresh the list so the updated status is reflected immediately
      const response = await purchaseOrderService.getPurchaseOrders({ pageNumber: 1, pageSize: 100 });
      const data = response?.data?.data;
      if (Array.isArray(data)) setOrders(data);
    } catch {
      setError(t('purchaseOrder.failedToSubmit'));
    } finally {
      setSubmittingId(null);
    }
  };

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  const totalCount     = orders.length;
  const draftCount     = orders.filter((o) => o.status === 'Draft').length;
  const pendingCount   = orders.filter((o) => o.approvalStatus === 'PendingApproval').length;
  const approvedCount  = orders.filter((o) => o.approvalStatus === 'Approved').length;

  const filteredOrders = activeFilter === 'draft'
    ? orders.filter((o) => o.status === 'Draft')
    : activeFilter === 'pending'
    ? orders.filter((o) => o.approvalStatus === 'PendingApproval')
    : activeFilter === 'approved'
    ? orders.filter((o) => o.approvalStatus === 'Approved')
    : orders;

  const handleStatFilter = (filter: 'all' | 'draft' | 'pending' | 'approved') => {
    setActiveFilter((prev) => prev === filter ? 'all' : filter);
  };

  const columns: GridColumn[] = [
    {
      key: 'poNumber',
      label: t('purchaseOrder.poNumber'),
      sortable: true,
      width: '140px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'supplierName',
      label: t('purchaseOrder.supplier'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value || 'N/A'}</strong>,
    },
    {
      key: 'orderDate',
      label: t('purchaseOrder.orderDate'),
      sortable: true,
      width: '130px',
      render: (value) => new Date(value).toLocaleDateString(langCode),
    },
    {
      key: 'expectedDeliveryDate',
      label: t('purchaseOrder.expectedDelivery'),
      sortable: true,
      width: '150px',
      hideTablet: true,
      render: (value) => new Date(value).toLocaleDateString(langCode),
    },
    {
      key: 'totalAmount',
      label: t('purchaseOrder.totalAmount'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      render: (value) =>
        value != null
          ? new Intl.NumberFormat(langCode, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
          : 'N/A',
    },
    {
      key: 'status',
      label: t('purchaseOrder.status'),
      sortable: true,
      width: '130px',
      render: (value: PurchaseOrderStatus) => (
        <span className={STATUS_BADGE[value] ?? 'ims-badge ims-badge-draft'}>
          {t(STATUS_I18N[value] ?? 'purchaseOrder.statusDraft')}
        </span>
      ),
    },
    {
      key: 'approvalStatus',
      label: t('purchaseOrder.approval.approvalColumn'),
      sortable: true,
      width: '160px',
      hideMobile: true,
      render: (value: ApprovalStatus, item: PurchaseOrderDTO) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className={APPROVAL_BADGE[value] ?? 'ims-badge ims-badge-draft'}>
            {t(`purchaseOrder.approval.status${value}`)}
          </span>
          {item.totalApprovalLevels > 0 && (
            <span className="po-approval-level-meta">
              {item.currentApprovalLevel}/{item.totalApprovalLevels}{' '}
              {t('purchaseOrder.approval.level').toLowerCase()}s
            </span>
          )}
        </div>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item: PurchaseOrderDTO) => navigate(`/purchase-orders/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    // Submit for approval — visible to anyone who can view (action is business workflow, not a separate permission)
    {
      icon: submittingId !== null ? (
        <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14, borderWidth: 2 }} />
      ) : (
        <SendHorizontal size={16} strokeWidth={1.75} />
      ),
      onClick: (item: PurchaseOrderDTO) => handleSubmitForApproval(item),
      className: 'grid-action-btn-submit',
      title: t('purchaseOrder.submitTooltip'),
      hidden: (item: PurchaseOrderDTO) =>
        item.status !== 'Draft' || item.approvalStatus !== 'Draft',
    },
    // PDF — always visible
    {
      icon: (
        <FileText size={18} strokeWidth={2} />
      ),
      onClick: (item: PurchaseOrderDTO) => {
        purchaseOrderService.openPdfInNewTab(item.id).catch((err: unknown) => {
          const msg = (err as Error)?.message ?? '';
          if (msg === 'popup_blocked') {
            setError(t('purchaseOrder.pdf.popupBlocked'));
          } else {
            setError(`${t('purchaseOrder.pdf.failedToOpen')} (${msg})`);
          }
        });
      },
      className: 'grid-action-btn-pdf',
      title: t('purchaseOrder.pdf.viewTooltip'),
    },
    ...(hasPermission(Permissions.PurchaseOrders.Edit)
      ? [{
          icon: (
            <PenLine size={18} strokeWidth={2} />
          ),
          onClick: (item: PurchaseOrderDTO) => navigate(`/purchase-orders/${item.id}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
        }]
      : []),
    ...(hasPermission(Permissions.PurchaseOrders.Delete)
      ? [{
          icon: (
            <Trash2 size={18} strokeWidth={2} />
          ),
          onClick: (item: PurchaseOrderDTO) => navigate(`/purchase-orders/${item.id}/delete`),
          className: 'grid-action-btn-delete',
          title: t('common.delete'),
        }]
      : []),
  ];

  return (
    <Layout>
      <div className="po-page">
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('purchaseOrder.title')}</h1>
          <div className="ims-list-actions">
            <PermissionGuard claim={Permissions.PurchaseOrders.Create}>
              <button
                className="btn-new"
                onClick={() => navigate('/purchase-orders/create')}
              >
                <CirclePlus/>
                {t('purchaseOrder.newPurchaseOrder')}
              </button>
            </PermissionGuard>
            <button
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/purchase-orders/outstanding')}
              title="View outstanding supplier payments"
            >
              <Banknote size={18} strokeWidth={2} />
              Outstanding Payments
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('purchaseOrder.refreshOrders')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('purchaseOrder.refreshOrders')}
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="ims-stats-row">
          <div
            className={`ims-stat-card ims-stat-card--clickable${activeFilter === 'all' ? ' ims-stat-card--active' : ''}`}
            onClick={() => handleStatFilter('all')}
            title={t('purchaseOrder.filterAll', 'Show all')}
          >
            <div className="ims-stat-icon ims-stat-icon-total">
              <FileText size={22} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{totalCount}</span>
              <span className="ims-stat-label">{t('purchaseOrder.totalOrders')}</span>
            </div>
          </div>
          <div
            className={`ims-stat-card ims-stat-card--clickable${activeFilter === 'draft' ? ' ims-stat-card--active' : ''}`}
            onClick={() => handleStatFilter('draft')}
            title={t('purchaseOrder.filterDraft', 'Filter by Draft')}
          >
            <div className="ims-stat-icon ims-stat-icon-draft">
              <FileText size={22} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{draftCount}</span>
              <span className="ims-stat-label">{t('purchaseOrder.statusDraft')}</span>
            </div>
          </div>
          <div
            className={`ims-stat-card ims-stat-card--clickable${activeFilter === 'pending' ? ' ims-stat-card--active' : ''}`}
            onClick={() => handleStatFilter('pending')}
            title={t('purchaseOrder.filterPending', 'Filter by Pending Approval')}
          >
            <div className="ims-stat-icon ims-stat-icon-pending">
              <Clock size={22} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{pendingCount}</span>
              <span className="ims-stat-label">{t('purchaseOrder.approval.statusPendingApproval')}</span>
            </div>
          </div>
          <div
            className={`ims-stat-card ims-stat-card--clickable${activeFilter === 'approved' ? ' ims-stat-card--active' : ''}`}
            onClick={() => handleStatFilter('approved')}
            title={t('purchaseOrder.filterApproved', 'Filter by Approved')}
          >
            <div className="ims-stat-icon ims-stat-icon-approved">
              <Check size={22} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{approvedCount}</span>
              <span className="ims-stat-label">{t('purchaseOrder.approval.statusApproved')}</span>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="ims-alert ims-alert-success">
            {successMessage}
            <button type="button" onClick={() => setSuccessMessage(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {error && (
          <div className="ims-alert ims-alert-error">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
              aria-label={t('common.close')}
            >✕</button>
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid
            data={filteredOrders}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('purchaseOrder.noOrdersFound')}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>
      </div>
    </Layout>
  );
};

export default PurchaseOrderListPage;
